import {
  BookOpen,
  File,
  FileArchive,
  FileImage,
  FileJson,
  FileSpreadsheet,
  FileText,
  FileType,
  Link2,
  NotebookText,
  Presentation,
  Sheet,
  type LucideIcon,
} from 'lucide-react';
import type { ResourceFileType } from '@lp/shared';

/**
 * What kind of file a reference document is, at a glance: an icon per family
 * and the extension spelled out beside it, so "XLSX" and "DOCX" are told apart
 * without opening anything. Reference files are download-only, so this is the
 * whole preview a learner gets.
 */

const ICONS: Record<ResourceFileType, LucideIcon> = {
  pdf: BookOpen,
  word: FileText,
  excel: FileSpreadsheet,
  powerpoint: Presentation,
  csv: Sheet,
  text: FileType,
  markdown: FileType,
  json: FileJson,
  notebook: NotebookText,
  image: FileImage,
  archive: FileArchive,
  other: File,
};

export const FILE_TYPE_NAMES: Record<ResourceFileType, string> = {
  pdf: 'PDF document',
  word: 'Word document',
  excel: 'Excel spreadsheet',
  powerpoint: 'PowerPoint presentation',
  csv: 'CSV data',
  text: 'Text file',
  markdown: 'Markdown file',
  json: 'JSON file',
  notebook: 'Jupyter notebook',
  image: 'Image',
  archive: 'Zip archive',
  other: 'File',
};

/** `XLSX` from `grades.xlsx`; null when there is no sensible extension. */
export function extensionLabel(fileName: string | null | undefined): string | null {
  if (!fileName) return null;
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0 || dot === fileName.length - 1) return null;
  const ext = fileName.slice(dot + 1);
  return ext.length <= 5 ? ext.toUpperCase() : null;
}

export function FileTypeIcon({
  type,
  fileName,
  link = false,
}: {
  type: ResourceFileType;
  fileName?: string | null;
  /** A web link rather than an uploaded file. */
  link?: boolean;
}) {
  const Icon = link ? Link2 : ICONS[type];
  const ext = link ? null : extensionLabel(fileName);
  return (
    <span className="flex w-12 shrink-0 flex-col items-center gap-1" title={link ? 'Link' : FILE_TYPE_NAMES[type]}>
      <span className="flex h-9 w-9 items-center justify-center rounded-control border border-line bg-surface-2 text-ink-muted">
        <Icon size={18} aria-hidden />
      </span>
      {ext ? (
        <span className="font-mono text-[10px] font-semibold leading-none tracking-wide text-ink-muted">
          {ext}
        </span>
      ) : null}
      <span className="sr-only">{link ? 'Link' : FILE_TYPE_NAMES[type]}</span>
    </span>
  );
}
